
import JigTooltip from '@/components/input/JigTooltip.vue'
import DataTable from '@/components/material/DataTable.vue'
import CategoryDialog from '@/components/modals/CategoryDialog.vue'
import GenericError from '@/components/snackbars/GenericError.vue'
import LibraryMixin from '@/mixin/Library'
import { DataTableHeadings } from '@/modules/library/types'
import { CategoryState, LibraryCategories, LibraryCategory, LibraryCategoryStates } from '@/store/modules/teamlibrary/types'
import { Namespace } from '@/store/types'
import { CategoryHelpers } from '@/utils/category-helpers'
import { Filters } from '@/utils/filters'
import IconArrowDown from '@svg/IconArrowDown.svg'
import IconArrowUp from '@svg/IconArrowUp.svg'
import IconPencil from '@svg/IconPencil.svg'
import IconPlusCircle from '@svg/IconPlusCircle.svg'
import IconReset from '@svg/IconReset.svg'
import { mixins } from 'vue-class-component'
import { Component, Watch } from 'vue-property-decorator'
import { Action, Getter, Mutation, State } from 'vuex-class'

// NOTE: This function has been moved to Filters.sortCategoriesByOrderAsc
// This export is kept for backward compatibility but should use Filters.sortCategoriesByOrderAsc instead
export function SortCategoriesByOrderAsc(a: LibraryCategory, b: LibraryCategory): number {
  return Filters.sortCategoriesByOrderAsc(a, b)
}

/**
 * Library Categories Management Page
 *
 * JigSpace Staff Only - This page allows JigSpace staff to create, edit, and re-order
 * public library categories that are shared across all tenants in the JigSpace platform.
 *
 * Features:
 * - Create new public categories
 * - Edit existing category details (name, state, order)
 * - Reorder categories using up/down arrows
 * - View category metadata (ID, updated date, state)
 */
@Component({
  components: {
    CategoryDialog,
    DataTable,
    GenericError,
    IconArrowDown,
    IconArrowUp,
    IconPencil,
    IconPlusCircle,
    IconReset,
    JigTooltip,
  },
})
export default class LibraryCategoriesVue extends mixins(LibraryMixin) {
  @State('apiError', { namespace: Namespace.Utils })
  public apiError!: string
  @State('libraryCategoryStates', { namespace: Namespace.TeamLibrary })
  public libraryCategoryStates!: LibraryCategoryStates
  @State('defaultLibraryCategory', { namespace: Namespace.TeamLibrary })
  public defaultLibraryCategory!: LibraryCategory
  @State('defaultLibraryCategoryState', { namespace: Namespace.TeamLibrary })
  public defaultLibraryCategoryState!: CategoryState | null

  @Getter('isPreparingApp', { namespace: Namespace.Utils })
  public isPreparingApp!: boolean

  @Action('loadLibraryCategoryStates', { namespace: Namespace.TeamLibrary })
  public loadLibraryCategoryStates: any
  @Action('upsertLibraryCategories', { namespace: Namespace.TeamLibrary })
  public upsertLibraryCategories: any
  @Mutation('addLibraryPublicCategory', { namespace: Namespace.TeamLibrary })
  public addLibraryPublicCategory: any
  @Mutation('updateLibraryPublicCategory', { namespace: Namespace.TeamLibrary })
  public updateLibraryPublicCategory: any
  @Mutation('replaceLibraryPublicCategories', { namespace: Namespace.TeamLibrary })
  public replaceLibraryPublicCategories: any
  @Mutation('refreshLibraryPublicCategories', { namespace: Namespace.TeamLibrary })
  public refreshLibraryPublicCategories: any
  @Mutation('setLibraryPublicCategoryOrder', { namespace: Namespace.TeamLibrary })
  public setLibraryPublicCategoryOrder: any

  public $refs!: Vue['$refs'] & {
    genericError: GenericError
  }

  private readonly unknownCategoryIndex: number = -1
  private readonly invalidCategoryId: number = -1
  private editedCategory: LibraryCategory | null = null
  private editedIndex: number = this.unknownCategoryIndex
  private dataDirty: boolean = false
  private readonly hiddenStateId = 5
  private readonly TestCategoryId = 9999
  private isLoadingLibraryCategories: boolean = false

  // New/Edit category dialog fields
  private dialogVisible: boolean = false
  private isSubmitting: boolean = false
  private libraryCategoryHeaders: DataTableHeadings = {
    CategoryId: {
      copy: 'ID',
      sort: false,
    },
    DisplayName: {
      copy: 'Category name',
      sort: false,
    },
    DateUpdated: {
      copy: 'Date updated',
      sort: false,
    },
    StateName: {
      copy: 'State',
      sort: false,
    },
    CategoryOrder: {
      copy: 'Display order',
      sort: false,
    },
    actions: {
      copy: 'Actions',
      sort: false,
    },
  }

  @Watch('initializing')
  private async onInitializingChanged(value: boolean) {
    if (value || this.isLoadingLibraryCategories) return

    this.prepareLibraryCategories()
  }

  protected mounted() {
    if (this.initializing || this.isLoadingLibraryCategories) return

    this.prepareLibraryCategories()
  }

  // Override the hook method
  protected onAfterTenantChanged(): void {
    this.prepareLibraryCategories()
  }

  private get preSortedCategories(): LibraryCategory[] {
    if (this.libraryPublicCategories != null && this.libraryPublicCategories.Categories != null) {
      return this.libraryPublicCategories.Categories.slice().sort(Filters.sortCategoriesByOrderAsc)
    }
    return []
  }

  private async prepareLibraryCategories() {
    this.isLoadingLibraryCategories = true
    this.loadLibraryCategories().then(() => this.loadLibraryCategoryStates())
    this.isLoadingLibraryCategories = false
  }

  private formatDate(value: string) {
    return Filters.formatDate(value)
  }

  private canMoveUp(category: LibraryCategory, sortedCats: LibraryCategory[]): boolean {
    return CategoryHelpers.canMoveUp(category, sortedCats)
  }

  private canMoveDown(category: LibraryCategory, sortedCats: LibraryCategory[]): boolean {
    return CategoryHelpers.canMoveDown(category, sortedCats)
  }

  private openEditCategoryDialog(category: LibraryCategory) {
    if (this.libraryPublicCategories == null) {
      return
    }
    this.editedIndex = this.libraryPublicCategories.Categories.indexOf(category)
    this.editedCategory = category
    this.dialogVisible = true
  }

  // openNewCategoryDialog - Open the new category dialog
  private openNewCategoryDialog() {
    this.editedIndex = this.unknownCategoryIndex
    this.editedCategory = null
    this.dialogVisible = true
  }

  private cancelDialog() {
    this.dialogVisible = false
    this.editedIndex = this.unknownCategoryIndex
    this.editedCategory = null
  }

  private getStateNameByStateId(stateId: number): string {
    if (this.libraryCategoryStates == null) {
      return ''
    }

    const stateIndex = this.libraryCategoryStates.States.findIndex((x: CategoryState) => x.StateId === stateId)

    if (stateIndex === -1) {
      return ''
    }

    return this.libraryCategoryStates.States[stateIndex].StateName
  }

  private async onCategorySubmit(categoryData: any) {
    if (this.libraryPublicCategories == null) {
      return
    }

    this.isSubmitting = true

    try {
      if (this.editedIndex === this.unknownCategoryIndex) {
        // Creating new category
        const catId = this.calculateNewCategoryId()

        if (catId === this.invalidCategoryId) {
          this.$refs.genericError.ShowError('Failed to generate a valid new Category Id')
          return
        }

        const newCategory: LibraryCategory = {
          CategoryId: catId,
          DisplayName: categoryData.DisplayName,
          CategoryOrder: categoryData.CategoryOrder,
          StateId: categoryData.StateId,
          StateName: categoryData.StateName,
          TenantId: this.myTenantID,
          DateCreated: new Date().toISOString(),
          DateUpdated: new Date().toISOString(),
        }

        this.addLibraryPublicCategory(newCategory)
      } else {
        // Editing existing category
        const existingIndex = this.libraryPublicCategories.Categories.findIndex(
          (x) => this.editedCategory != null && x.CategoryId === this.editedCategory.CategoryId
        )

        if (existingIndex === this.unknownCategoryIndex) {
          this.$refs.genericError.ShowError(`Category edit failed. Unable to update category data model, unknown category index.`)
          return
        }

        this.updateLibraryPublicCategory({
          index: existingIndex,
          updates: {
            DisplayName: categoryData.DisplayName,
            CategoryOrder: categoryData.CategoryOrder,
            StateId: categoryData.StateId,
            StateName: categoryData.StateName,
          },
        })
        this.resortDataTable()
      }

      this.recalculateCategoryOrderList()
      this.dataDirty = true
      this.dialogVisible = false
      this.editedIndex = this.unknownCategoryIndex
      this.editedCategory = null
    } finally {
      this.isSubmitting = false
    }
  }

  private resortDataTable() {
    // Hack - Because our v-data-table is watching the categories array for changes and not the contents of the
    // items in the array, we need to trick the v-data-table into resorting.
    if (this.libraryPublicCategories !== undefined) {
      this.refreshLibraryPublicCategories()
    }
  }

  private recalculateCategoryOrderList() {
    if (this.libraryPublicCategories == null) {
      return
    }

    const newOrder = CategoryHelpers.recalculateCategoryOrderList(this.libraryPublicCategories.Categories, this.hiddenStateId, true)

    this.setLibraryPublicCategoryOrder(newOrder)
  }

  // calculateNewCategoryId scans the existing category ids and determines what the next
  // category id should be for the next new category entry
  private calculateNewCategoryId(): number {
    let newCategoryId = this.invalidCategoryId
    if (this.libraryPublicCategories != null && this.libraryPublicCategories.Categories != null) {
      newCategoryId = 1
      for (const c of this.libraryPublicCategories.Categories) {
        if (c.CategoryId >= newCategoryId && c.CategoryId !== this.TestCategoryId) {
          newCategoryId = c.CategoryId + 1
        }
      }
      if (newCategoryId === this.TestCategoryId) {
        newCategoryId++
      }
    }
    return newCategoryId
  }

  private moveCategory(category: LibraryCategory, direction: number) {
    if (this.libraryPublicCategories == null) {
      return
    }

    const updatedCategories = this.libraryPublicCategories.Categories.map((item) => ({
      ...item,
    }))
    const targetCategory = updatedCategories.find((item) => item.CategoryId === category.CategoryId)
    if (targetCategory == null) {
      return
    }

    const success = CategoryHelpers.moveCategory(targetCategory, direction, updatedCategories, this.unknownCategoryIndex)

    if (success) {
      this.replaceLibraryPublicCategories(updatedCategories)
      this.recalculateCategoryOrderList()
      this.dataDirty = true
      this.resortDataTable()
    }
  }

  private async saveChanges() {
    if (!this.dataDirty || this.libraryPublicCategories == null) {
      return
    }

    if (
      await this.upsertLibraryCategories({
        categories: this.libraryPublicCategories.Categories,
      })
    ) {
      this.dataDirty = false
    }
  }

  private async resetData() {
    const p = this.loadLibraryCategories().then(() => this.loadLibraryCategoryStates())
    if (await p) {
      this.dataDirty = false
    }
  }
}
